import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container-0g">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            <p className="text-slate-500 mb-8">Last updated: March 14, 2026</p>
            
            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 mb-8">
                This Privacy Policy explains how 0g Baltics ("we", "us") processes personal data in connection with our IoT solutions website and services. Our solutions are designed for industrial and municipal operations. We are committed to protecting your privacy and ensuring compliance with applicable data protection regulations.
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Controller</h2>
                <div className="bg-slate-50 p-6 rounded-sm border border-slate-200">
                  <p className="mb-2"><strong>Controller:</strong> 0g Baltics SIA / 0g Baltics UAB / E2 Technologies OÜ</p>
                  <p className="mb-2"><strong>Addresses:</strong></p>
                  <ul className="list-none pl-0 mb-2 space-y-1">
                    <li>Lithuania: A. Goštauto g. 8-358, LT-01108 Vilnius</li>
                    <li>Latvia: Elizabetes iela 31-8, LV-1010 Riga</li>
                    <li>Estonia: Lõõtsa tn 5, 11415 Tallinn</li>
                  </ul>
                  <p><strong>Email:</strong> <a href="mailto:info@0gbaltics.com" className="text-teal-600 hover:underline">info@0gbaltics.com</a></p>
                </div>
                <p className="mt-4 text-slate-600">
                  For visitors in the EU/EEA, this policy is intended to comply with the General Data Protection Regulation (GDPR). For visitors in the Baltic states, processing is also aligned with local data protection laws.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Scope</h2>
                <p className="text-slate-600 mb-4">This policy applies to:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Website visits (landing pages, contact forms, demo access requests)</li>
                  <li>Demo platform access (authentication and usage)</li>
                  <li>Business communications related to 0g Baltics IoT solutions</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Personal Data We Collect</h2>
                <p className="text-slate-600 mb-4">Depending on interaction, we may collect:</p>
                
                <h3 className="text-xl font-medium text-slate-800 mt-6 mb-3">A) Contact Form Data</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>Name, organization, role</li>
                  <li>Email, optional phone number</li>
                  <li>Country, interest area</li>
                  <li>Message content</li>
                  <li>Whether you represent a city/municipality or are an integrator</li>
                  <li>Consent status and timestamp</li>
                </ul>

                <h3 className="text-xl font-medium text-slate-800 mt-6 mb-3">B) Demo Platform Data</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>Authentication data (email, login metadata)</li>
                  <li>Basic usage logs for security and service improvement</li>
                </ul>

                <h3 className="text-xl font-medium text-slate-800 mt-6 mb-3">C) Technical Data</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>IP address, browser and device information</li>
                  <li>Approximate location (country/city level)</li>
                  <li>Website analytics data (if enabled)</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Purposes and Legal Bases</h2>
                <p className="text-slate-600 mb-4">Personal data is processed for the following purposes:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-3">
                  <li>
                    <strong>Responding to inquiries and providing services</strong><br/>
                    Legal basis: GDPR Art. 6(1)(b) and/or Art. 6(1)(f)
                  </li>
                  <li>
                    <strong>Business communication and follow-up</strong><br/>
                    Legal basis: Art. 6(1)(f)
                  </li>
                  <li>
                    <strong>Security, fraud prevention, and service integrity</strong><br/>
                    Legal basis: Art. 6(1)(f)
                  </li>
                  <li>
                    <strong>Compliance with legal obligations</strong><br/>
                    Legal basis: Art. 6(1)(c)
                  </li>
                  <li>
                    <strong>Marketing communications (where applicable and permitted)</strong><br/>
                    Legal basis: Art. 6(1)(a) or Art. 6(1)(f)<br/>
                    You may opt out at any time.
                  </li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Sharing</h2>
                <p className="text-slate-600 mb-4">Personal data may be shared with:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Hosting, infrastructure, and authentication providers</li>
                  <li>Analytics providers (if enabled)</li>
                  <li>Legal or professional advisors</li>
                  <li>Public authorities where legally required</li>
                </ul>
                <p className="mt-4 text-slate-600 font-medium">We do not sell personal data.</p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. International Data Transfers</h2>
                <p className="text-slate-600">
                  Where personal data is transferred outside the EU/EEA, appropriate safeguards are applied, including EU Standard Contractual Clauses (SCCs). Data transfers within the Baltic states and EU are covered by GDPR provisions.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Data Retention</h2>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Contact requests:</strong> typically retained for 12–24 months after last interaction</li>
                  <li><strong>Demo accounts and logs:</strong> retained for the duration of access and a limited audit period</li>
                  <li><strong>Legal records:</strong> retained as required by law</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Security Measures</h2>
                <p className="text-slate-600">
                  Appropriate technical and organizational measures are implemented, including access control, encryption in transit, and monitoring.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Data Subject Rights</h2>
                <p className="text-slate-600 mb-4">You may have the right to:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Access your personal data</li>
                  <li>Rectify inaccurate data</li>
                  <li>Delete your data</li>
                  <li>Restrict processing</li>
                  <li>Object to processing</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent</li>
                </ul>
                <p className="mt-4 text-slate-600">
                  Requests can be sent to: <a href="mailto:info@0gbaltics.com" className="text-teal-600 hover:underline">info@0gbaltics.com</a>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Supervisory Authorities</h2>
                <p className="text-slate-600 mb-4">EU/EEA users may lodge a complaint with their local supervisory authority:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Lithuania:</strong> State Data Protection Inspectorate (VDAI)</li>
                  <li><strong>Latvia:</strong> Data State Inspectorate (DVI)</li>
                  <li><strong>Estonia:</strong> Data Protection Inspectorate (AKI)</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Cookies and Analytics</h2>
                <p className="text-slate-600">
                  Cookies may be used to ensure functionality and improve the website. Cookie preferences can be managed via browser settings or consent tools where applicable.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Changes to This Policy</h2>
                <p className="text-slate-600">
                  This policy may be updated from time to time. The "Last updated" date reflects the latest revision.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
